// utilities/projectExporter.js

// دالة لتحديث الإصدار الرئيسي عند التصدير
const incrementMajorVersion = (currentVersion) => {
  try {
    const versionParts = currentVersion.split('.').map(Number);
    versionParts[0] = (versionParts[0] || 1) + 1; // زيادة الإصدار الرئيسي
    versionParts[1] = 0; // إعادة تعيين الإصدار الثانوي
    versionParts[2] = 0; // إعادة تعيين الإصدار الفرعي
    return versionParts.join('.');
  } catch (error) {
    return '2.0.0'; // إصدار افتراضي في حالة الخطأ
  }
};

// دالة لحفظ الإصدار المحدث في المشروع
const updateProjectVersion = (projectId, newVersion) => {
  try {
    // تحديث بيانات المشروع
    const projectData = localStorage.getItem(`cad_project:${projectId}`);
    if (projectData) {
      const parsedData = JSON.parse(projectData);
      if (parsedData.projectInfo) {
        parsedData.projectInfo.version = newVersion;
        parsedData.projectInfo.lastModified = new Date().toISOString();
      }
      localStorage.setItem(`cad_project:${projectId}`, JSON.stringify(parsedData));
    }

    // تحديث قائمة المشاريع
    const projectsList = JSON.parse(localStorage.getItem('cad_projects_list') || '[]');
    const updatedList = projectsList.map(p => 
      p.id === projectId 
        ? { ...p, version: newVersion, lastModified: new Date().toISOString() }
        : p
    );
    localStorage.setItem('cad_projects_list', JSON.stringify(updatedList));
  } catch (error) {
    console.error('خطأ في تحديث إصدار المشروع:', error);
  }
};

// دالة لإنشاء اسم ملف ذكي
const generateSmartFileName = (projectInfo, exportType = 'complete') => {
  try {
    // اسم المشروع مع تنظيف الأحرف الخاصة
    const projectName = (projectInfo.projectName || 'مشروع_جديد')
      .replace(/[<>:"/\\|?*]/g, '') // إزالة الأحرف المحظورة في أسماء الملفات
      .replace(/\s+/g, '_') // استبدال المسافات بشرطات سفلية
      .substring(0, 50); // تحديد الطول الأقصى

    // التاريخ والوقت
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0]; // YYYY-MM-DD
    const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '-'); // HH-MM-SS

    // الإصدار
    const version = projectInfo.version || '1.0.0';

    // نوع التصدير
    const typePrefix = exportType === 'quick' ? 'سريع_' : exportType === 'page' ? 'صفحة_' : '';

    // تجميع اسم الملف
    const fileName = `${typePrefix}${projectName}_v${version}_${dateStr}_${timeStr}`;
    
    return fileName;
  } catch (error) {
    console.warn('خطأ في إنشاء اسم الملف:', error);
    // اسم احتياطي
    const timestamp = Date.now();
    return `مشروع_${timestamp}`;
  }
};

export const exportCompleteProject = (projectId, projectName = 'مشروع') => {
  try {
    // جمع جميع البيانات
    const projectData = localStorage.getItem(`cad_project:${projectId}`);
    if (!projectData) {
      throw new Error('لا توجد بيانات للمشروع المحدد');
    }

    const parsedProjectData = JSON.parse(projectData);
    
    // جمع معلومات المشروع من قائمة المشاريع
    const projectsList = JSON.parse(localStorage.getItem('cad_projects_list') || '[]');
    const projectInfo = projectsList.find(p => p.id === projectId);

    // تحديث الإصدار الرئيسي قبل التصدير
    const currentVersion = projectInfo?.version || parsedProjectData.projectInfo?.version || '1.0.0';
    const newVersion = incrementMajorVersion(currentVersion);
    
    // حفظ الإصدار الجديد
    updateProjectVersion(projectId, newVersion);

    // إنشاء ملف التصدير الشامل
    const exportData = {
      // معلومات التصدير
      exportInfo: {
        version: '2.0',
        exportedAt: new Date().toISOString(),
        exportedBy: 'CAD Drawing Application',
        format: 'Complete Project Export'
      },
      
      // معلومات المشروع المحدثة
      projectInfo: {
        id: projectId,
        name: projectInfo?.name || projectName,
        description: projectInfo?.description || '',
        createdAt: projectInfo?.createdAt || new Date().toISOString(),
        lastModified: new Date().toISOString(),
        version: newVersion
      },
      
      // بيانات المشروع الكاملة
      projectData: {
        // الصفحات وبياناتها
        pagesById: parsedProjectData.pagesById || {},
        pageOrder: parsedProjectData.pageOrder || [],
        activePageId: parsedProjectData.activePageId || 'floor',
        
        // إعدادات المشروع
        settings: {
          snapToGrid: parsedProjectData.snapToGrid || true,
          gridSize: parsedProjectData.gridSize || 5,
          selectedAngleDirection: parsedProjectData.selectedAngleDirection || 'horizontal_right_up'
        },
        
        // معلومات المشروع التفصيلية
        projectInfo: parsedProjectData.projectInfo || {
          projectNumber: '',
          clientName: '',
          address: '',
          phone: '',
          measuredBy: '',
          branch: ''
        },
        
        // قائمة الأجهزة
        devices: parsedProjectData.devices || [],
        deviceNote: parsedProjectData.deviceNote || '',
        
        // بيانات إضافية
        metadata: {
          totalElements: countTotalElements(parsedProjectData.pagesById || {}),
          totalPages: Object.keys(parsedProjectData.pagesById || {}).length,
          lastSaved: parsedProjectData.lastModified || new Date().toISOString()
        }
      }
    };

    // إنشاء اسم ملف ذكي مع الإصدار الجديد
    const smartFileName = generateSmartFileName({ 
      projectName: projectInfo?.name || projectName, 
      version: newVersion 
    }, 'complete');
    
    // إنشاء الملف وتحميله
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { 
      type: 'application/json;charset=utf-8' 
    });
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${smartFileName}.cadproject`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    return {
      success: true,
      message: 'تم تصدير المشروع بنجاح',
      fileName: `${smartFileName}.cadproject`
    };

  } catch (error) {
    console.error('خطأ في تصدير المشروع:', error);
    return {
      success: false,
      message: `فشل في تصدير المشروع: ${error.message}`
    };
  }
};

// دالة لعد العناصر الإجمالية في جميع الصفحات
const countTotalElements = (pages) => {
  return Object.values(pages).reduce((total, page) => {
    return total + (page.elements ? page.elements.length : 0);
  }, 0);
};

// تصدير سريع (نسخة مبسطة)
export const exportQuickProject = (projectId, projectName = 'مشروع') => {
  try {
    // جمع معلومات المشروع
    const projectsList = JSON.parse(localStorage.getItem('cad_projects_list') || '[]');
    const projectInfo = projectsList.find(p => p.id === projectId);
    
    // تحديث الإصدار الرئيسي قبل التصدير
    const currentVersion = projectInfo?.version || '1.0.0';
    const newVersion = incrementMajorVersion(currentVersion);
    
    // حفظ الإصدار الجديد
    updateProjectVersion(projectId, newVersion);
    
    // جمع بيانات المشروع
    const projectData = localStorage.getItem(`cad_project:${projectId}`);
    if (!projectData) {
      throw new Error('لا توجد بيانات للمشروع المحدد');
    }

    // إنشاء اسم ملف ذكي مع الإصدار الجديد
    const smartFileName = generateSmartFileName({ 
      projectName: projectInfo?.name || projectName, 
      version: newVersion 
    }, 'quick');

    const blob = new Blob([projectData], { 
      type: 'application/json;charset=utf-8' 
    });
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${smartFileName}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    return {
      success: true,
      message: 'تم تصدير المشروع (النسخة السريعة) بنجاح'
    };

  } catch (error) {
    console.error('خطأ في التصدير السريع:', error);
    return {
      success: false,
      message: `فشل في التصدير السريع: ${error.message}`
    };
  }
};

// تصدير صفحة واحدة فقط
export const exportSinglePage = (projectId, pageId, pageName = 'صفحة') => {
  try {
    // جمع معلومات المشروع
    const projectsList = JSON.parse(localStorage.getItem('cad_projects_list') || '[]');
    const projectInfo = projectsList.find(p => p.id === projectId);
    
    // تحديث الإصدار الرئيسي قبل التصدير
    const currentVersion = projectInfo?.version || '1.0.0';
    const newVersion = incrementMajorVersion(currentVersion);
    
    // حفظ الإصدار الجديد
    updateProjectVersion(projectId, newVersion);
    
    const projectData = localStorage.getItem(`cad_project:${projectId}`);
    if (!projectData) {
      throw new Error('لا توجد بيانات للمشروع المحدد');
    }

    const parsedData = JSON.parse(projectData);
    const pageData = parsedData.pagesById?.[pageId];
    
    if (!pageData) {
      throw new Error('لا توجد بيانات للصفحة المحددة');
    }

    const exportData = {
      exportInfo: {
        version: '2.0',
        exportedAt: new Date().toISOString(),
        type: 'Single Page Export'
      },
      pageData: pageData,
      pageId: pageId,
      pageName: pageName
    };

    // إنشاء اسم ملف ذكي للصفحة مع الإصدار الجديد
    const baseInfo = { 
      projectName: `${projectInfo?.name || 'مشروع'}_${pageName}`,
      version: newVersion
    };
    const smartFileName = generateSmartFileName(baseInfo, 'page');

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { 
      type: 'application/json;charset=utf-8' 
    });
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${smartFileName}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    return {
      success: true,
      message: `تم تصدير الصفحة "${pageName}" بنجاح`
    };

  } catch (error) {
    console.error('خطأ في تصدير الصفحة:', error);
    return {
      success: false,
      message: `فشل في تصدير الصفحة: ${error.message}`
    };
  }
};